// Noise that repeats every WORLD_W without a seam at the antimeridian, for the parchment and fog.
//
// tsl/noise.ts wraps its lattice with `mod(i, period)`. On the GPU that division is a reciprocal
// multiply, so mod(488, 488) comes out as 488, not 0: the last lattice cell before the seam blends
// toward a different hash than the first cell after it, and the fog shows a hard vertical line at
// 180° (a whole cell wide). Same lattice and hash here, with a wrap that corrects the rounding.
//
//     x ─► x · period / WORLD_W ─► floor ─► wrapCell ─► hash
//
// Delete this file once tsl/noise.ts wraps with wrapCell (see the contract request).

import { Fn, fract, floor, float, mix, select, vec2 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { WORLD_W } from '../../core/grid.ts';
import { hash12, periodFor } from '../tsl/noise.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;

// Brush strokes: long along x, short across, sheared so they lean (as tsl/noise.ts strokes).
const STROKE_ALONG = 0.35;
const STROKE_ACROSS = 1.6;
const STROKE_SHEAR = 0.45;
const STROKE_LEVELS = 5;
const STROKE_QUANT = 0.7;

// Octave offsets and weights of the three-octave fbm (as tsl/noise.ts worldFbm).
const OCTAVE_B_Y = 17.1;
const OCTAVE_C_Y = 3.7;
const FBM_NORM = 0.875;

/** A lattice index wrapped into [0, period), exact when the division rounds either way. */
function wrapCell(index: F, period: F): F {
  const wrapped = index.sub(period.mul(floor(index.div(period))));
  return select(wrapped.greaterThanEqual(period), wrapped.sub(period), select(wrapped.lessThan(0), wrapped.add(period), wrapped));
}

/** Value noise in [0, 1), periodic in x with an integer `period` of lattice cells. */
const vnoiseW = Fn(([p, period]: [V2, F]) => {
  const i = floor(p);
  const f = fract(p);
  const u = f.mul(f).mul(f.mul(-2).add(3));
  const x0 = wrapCell(i.x, period);
  const x1 = wrapCell(i.x.add(1), period);
  const a = hash12(vec2(x0, i.y));
  const b = hash12(vec2(x1, i.y));
  const c = hash12(vec2(x0, i.y.add(1)));
  const d = hash12(vec2(x1, i.y.add(1)));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}).setLayout({ name: 'wc_vnoiseW', type: 'float', inputs: [{ name: 'p', type: 'vec2' }, { name: 'period', type: 'float' }] });

/** Seamless value noise at world XZ: `freq` cells per world unit, rounded to whole cells. */
export function noiseW(xz: V2, freq: number): F {
  const period = periodFor(freq);
  return vnoiseW(xz.mul(period / WORLD_W), float(period));
}

/** Three-octave fbm of noiseW, normalised to [0, 1). */
export function fbmW(xz: V2, freq: number): F {
  const a = noiseW(xz, freq);
  const b = noiseW(xz.add(vec2(0, OCTAVE_B_Y)), freq * 2);
  const c = noiseW(xz.add(vec2(0, OCTAVE_C_Y)), freq * 4);
  return a.mul(0.5).add(b.mul(0.25)).add(c.mul(0.125)).div(FBM_NORM);
}

/** Brush strokes: noise stretched along x and partly quantised into flat patches, seamless in x. */
export function strokesW(xz: V2, freq: number): F {
  const periodU = periodFor(freq * STROKE_ALONG);
  const su = periodU / WORLD_W;
  const u = xz.x.mul(su).add(xz.y.mul(su * STROKE_SHEAR));
  const v = xz.y.mul(freq * STROKE_ACROSS);
  const p = vec2(u, v);
  const n = vnoiseW(p, float(periodU)).mul(0.5)
    .add(vnoiseW(p.mul(2).add(vec2(0, OCTAVE_B_Y)), float(periodU * 2)).mul(0.25))
    .add(vnoiseW(p.mul(4).add(vec2(0, OCTAVE_C_Y)), float(periodU * 4)).mul(0.125))
    .div(FBM_NORM);
  const q = floor(n.mul(STROKE_LEVELS).add(0.5)).div(STROKE_LEVELS);
  return mix(n, q, STROKE_QUANT);
}
